import { ReactNode } from 'react';
import { isEmpty } from 'fp-ts/Record';
import { FieldErrors } from 'react-hook-form';
import { OptimusProvider, QueryBoundary } from '@mews-commander/ui';
import { Badge, Button, Icon, IconName, Stack } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { lt, useToggle } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';

interface LedgersFiltersPanelWrapperProps {
    children: ReactNode;
    resetFilters: () => void;
    submit: () => void;
    filterPanelFiltersAreApplied: boolean;
    title: React.ReactNode;
    errors: FieldErrors;
    onClose?: () => void;
}

export const LedgersFiltersPanelWrapper = ({
    children,
    resetFilters,
    submit,
    filterPanelFiltersAreApplied,
    title,
    errors,
    onClose,
}: LedgersFiltersPanelWrapperProps) => {
    const [showFiltersSidebar, toggleFiltersSidebar] = useToggle({ initialValue: false });

    const handleClose = () => {
        onClose?.();
        toggleFiltersSidebar();
    };

    const handleSubmit = () => {
        submit?.();
        toggleFiltersSidebar();
    };

    return (
        <>
            <Button onClick={toggleFiltersSidebar} size="small" type="button">
                <Badge active={filterPanelFiltersAreApplied} outline data-test-id="filters-applied-badge">
                    <Icon value={IconName.Filter} size="small" />
                </Badge>
                {lt(TextKey.Filter)}
            </Button>
            <FloatingPanel
                size="small"
                active={showFiltersSidebar}
                onClose={handleClose}
                title={title}
                footer={{
                    primary: (
                        <Button
                            onClick={handleSubmit}
                            type="submit"
                            size="medium"
                            variant="primary"
                            disabled={!isEmpty(errors)}
                        >
                            {lt(TextKey.ApplyFilters)}
                        </Button>
                    ),
                    secondary: (
                        <Button
                            icon={IconName.Refresh}
                            onClick={resetFilters}
                            type="button"
                            variant="ghost"
                            size="medium"
                        >
                            {lt(TextKey.ResetFilters)}
                        </Button>
                    ),
                }}
            >
                <QueryBoundary loader="inline">
                    <OptimusProvider density="spacious">
                        <Stack spacing="300" vertical>
                            {children}
                        </Stack>
                    </OptimusProvider>
                </QueryBoundary>
            </FloatingPanel>
        </>
    );
};
